// @file Pinia store for the starting state of the surface
import { isAppUsing } from '@@/bits/flip'
import { PadletStartingStateApi } from '@@/surface/api/padlet_starting_state'
import type { OnboardingWallViz, User } from '@@/types'
import { blankState } from '@@/vuexstore/surface'
import type { SurfaceState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useSurfaceStartingStateStore = defineStore('surfaceStartingState', () => {
  const startingState = ref<SurfaceState>({ ...blankState() })

  const arvoConfig = computed(() => startingState.value.arvoConfig)
  const googleApiKeyForMap = computed<string>(() => startingState.value.constants.googleApiKeyForMap)
  const currentCountryCode = computed<string>(() => startingState.value.constants.currentCountryCode)
  const currentCountry = computed<string>(() => startingState.value.constants.currentCountry)
  const morpheusToken = computed<string>(() => startingState.value.constants.morpheusToken)
  const canMakeInTenant = computed<boolean>(() => startingState.value.canMakeInTenant)
  const user = computed<User>(() => startingState.value.user)

  // #region wall layouts
  const allLayouts = computed<OnboardingWallViz[]>(() => {
    const layouts = startingState.value.constants.allLayouts as OnboardingWallViz[]
    const filteredLayouts = layouts.filter((layout) => {
      if (!isAppUsing('tableLayout') && layout.value === 'table') return false
      return true
    })
    return filteredLayouts
  })

  const allLayoutsByGroup = computed(() => {
    const groups = startingState.value.constants.allLayoutsByGroup
    const filterLayout = (layout: { value: string }): boolean => {
      if (!isAppUsing('tableLayout') && layout.value === 'table') return false
      return true
    }

    return {
      simple: groups.simple.filter(filterLayout),
      advanced: groups.advanced.filter(filterLayout),
    }
  })

  const sectionableLayouts = computed<string[]>(() => {
    // some pages like mobile wish frames use this store without the full starting state like surface so some data is missing
    // they don't need this data anyways so just return an empty array for now
    if (startingState.value.constants.sectionableLayouts == null) return []
    return startingState.value.constants.sectionableLayouts.filter((layout) => {
      if (!isAppUsing('tableLayout') && layout === 'table') return false
      return true
    })
  })
  // #endregion

  function intializeState(state: SurfaceState): void {
    startingState.value = state
  }

  async function fetchStartingState(): Promise<void> {
    const response = await PadletStartingStateApi.fetch()
    startingState.value = Object.assign(startingState.value, response)
  }

  return {
    startingState,
    arvoConfig,
    googleApiKeyForMap,
    currentCountryCode,
    currentCountry,
    morpheusToken,
    canMakeInTenant,
    intializeState,
    fetchStartingState,
    user,

    // Layouts
    allLayouts,
    allLayoutsByGroup,
    sectionableLayouts,
  }
})
